package se.mittpass.web.components

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.dom.*

enum class PlannerFilter(val label: String, val icon: String) {
    ALL("Alla", "fa-solid fa-layer-group"),
    SHIFTS("Pass", "fa-solid fa-calendar-days"),
    VACATIONS("Semester", "fa-solid fa-umbrella-beach")
}

@Composable
internal fun SchemaToolbar(
    viewMode: ViewMode,
    onViewModeChange: (ViewMode) -> Unit,
    anchor: String,
    onAnchorChange: (String) -> Unit,
    isMobile: Boolean,
    isManager: Boolean,
    onCopyPrevWeek: (() -> Unit)? = null,
    filter: PlannerFilter = PlannerFilter.ALL,
    onFilterChange: (PlannerFilter) -> Unit = {}
) {
    val centerLabel = when (viewMode) {
        ViewMode.WEEK -> {
            val ws = mondayOf(anchor)
            val we = addDays(ws, 6)
            "v.${weekNumber(ws)} \u00B7 ${shortDate(ws)} \u2013 ${shortDate(we)}"
        }
        ViewMode.MONTH -> {
            val parts = anchor.split("-")
            "${MONTH_NAMES[parts[1].toInt() - 1].replaceFirstChar { it.uppercase() }} ${parts[0]}"
        }
        ViewMode.YEAR -> anchor.take(4)
    }

    fun stepAnchor(forward: Boolean) {
        val sign = if (forward) 1 else -1
        onAnchorChange(when (viewMode) {
            ViewMode.WEEK -> addDays(mondayOf(anchor), 7 * sign)
            ViewMode.MONTH -> addMonths(anchor, sign)
            ViewMode.YEAR -> "${anchor.take(4).toInt() + sign}-01-01"
        })
    }

    Div({ style {
        property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between")
        property("margin-bottom", "1.25rem"); property("gap", "0.75rem"); property("flex-wrap", "wrap")
    } }) {
        // Left: Navigation & Today
        Div({ style {
            property("display", "flex"); property("align-items", "center"); property("gap", "0.35rem")
        } }) {
            ToolbarIconBtn("fa-solid fa-chevron-left", "Föregående") { stepAnchor(false) }
            Button({
                onClick { onAnchorChange(todayLocalDate()) }
                style {
                    property("background", "var(--rp-overlay)"); property("border", "none")
                    property("border-radius", "10px"); property("padding", "0.4rem 0.85rem")
                    property("font-size", "0.825rem"); property("font-weight", "600")
                    property("color", "var(--rp-subtle)"); property("cursor", "pointer")
                }
            }) { Text("Idag") }
            ToolbarIconBtn("fa-solid fa-chevron-right", "Nästa") { stepAnchor(true) }
        }

        // Center: Date Label
        Div({ style {
            property("font-size", if (isMobile) "0.95rem" else "1.1rem")
            property("font-weight", "700"); property("color", "var(--rp-text)")
            property("letter-spacing", "-0.2px")
            property("flex", if (isMobile) "1 1 100%" else "1")
            property("text-align", if (isMobile) "left" else "center")
            property("padding", if (isMobile) "0.25rem 0.25rem 0" else "0 0.5rem")
        } }) { Text(centerLabel) }

        // Right: filter mode + view toggle
        Div({ style {
            property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem")
            property("flex-wrap", "wrap")
        } }) {
            FilterSegmented(filter, onFilterChange, isMobile)
            ViewSegmented(viewMode, onViewModeChange, isMobile)
        }
    }
}

@Composable
internal fun ToolbarIconBtn(icon: String, title: String, onClick: () -> Unit) {
    Button({
        onClick { onClick() }
        attr("title", title)
        style {
            property("background", "transparent"); property("border", "none"); property("color", "var(--rp-subtle)")
            property("width", "36px"); property("height", "36px"); property("border-radius", "8px")
            property("cursor", "pointer"); property("display", "flex")
            property("align-items", "center"); property("justify-content", "center")
        }
    }) { FaIcon(icon, size = "0.85rem") }
}

@Composable
internal fun ToolbarTextBtn(label: String, onClick: () -> Unit) {
    Button({
        onClick { onClick() }
        style {
            property("background", "transparent"); property("border", "none"); property("color", "var(--rp-pine)")
            property("padding", "0 0.85rem"); property("height", "36px"); property("border-radius", "8px")
            property("cursor", "pointer"); property("font-weight", "700"); property("font-size", "0.875rem")
            property("font-family", "inherit")
        }
    }) { Text(label) }
}

@Composable
internal fun ViewSegmented(current: ViewMode, onChange: (ViewMode) -> Unit, isMobile: Boolean) {
    Div({ style {
        property("display", "inline-flex"); property("background", "var(--rp-overlay)")
        property("border-radius", "12px"); property("padding", "3px"); property("gap", "2px")
    } }) {
        ViewMode.values().forEach { mode ->
            val active = mode == current
            Button({
                attr("data-testid", "viewmode-${mode.name}")
                onClick { onChange(mode) }
                style {
                    property("background", if (active) "var(--rp-surface)" else "transparent")
                    property("color", if (active) "var(--rp-pine)" else "var(--rp-subtle)")
                    property("border", "none"); property("border-radius", "9px")
                    property("padding", if (isMobile) "0.45rem 0.7rem" else "0.45rem 0.95rem")
                    property("cursor", "pointer"); property("font-weight", if (active) "700" else "600")
                    property("font-size", "0.85rem"); property("font-family", "inherit")
                    property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem")
                    if (active) property("box-shadow", "0 1px 3px rgba(13,13,24,0.08)")
                }
            }) {
                if (!isMobile) FaIcon(mode.icon, size = "0.75rem", color = if (active) "var(--rp-pine)" else "var(--rp-muted)")
                Text(mode.label)
            }
        }
    }
}

@Composable
internal fun FilterSegmented(current: PlannerFilter, onChange: (PlannerFilter) -> Unit, isMobile: Boolean) {
    Div({ style {
        property("display", "inline-flex"); property("background", "var(--rp-overlay)")
        property("border-radius", "12px"); property("padding", "3px"); property("gap", "2px")
    } }) {
        PlannerFilter.values().forEach { f ->
            val active = f == current
            Button({
                onClick { onChange(f) }
                style {
                    property("background", if (active) "var(--rp-pine)" else "transparent")
                    property("color", if (active) "#FFFFFF" else "var(--rp-subtle)")
                    property("border", "none"); property("border-radius", "9px")
                    property("padding", if (isMobile) "0.45rem 0.65rem" else "0.45rem 0.9rem")
                    property("cursor", "pointer"); property("font-weight", if (active) "700" else "600")
                    property("font-size", "0.85rem"); property("font-family", "inherit")
                    property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem")
                    if (active) property("box-shadow", "0 1px 3px rgba(108,60,225,0.2)")
                }
            }) {
                if (!isMobile) FaIcon(f.icon, size = "0.75rem", color = if (active) "var(--rp-surface)" else "var(--rp-muted)")
                Text(f.label)
            }
        }
    }
}
